import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import ImageUploader from "../../components/ImageUploader";
import { getProjectByIdApi, getProjectListApi } from "../../api/finance.project.api";
import { getProjectApartmentsApi } from "../../api/finance.apartment.api";
import { getCustomerListApi } from "../../api/finance.customer.api";
import { createBookingApi, getBookingByIdApi, updateBookingApi } from "../../api/finance.booking.api";
import { createLockingApi } from "../../api/finance.locking.api";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#DCE1EA";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const GRAD = "linear(to-r, #68A2FF, #2168DA)";

const Label = ({ children, required }) => (
  <Text fontSize="13px" fontWeight={600} mb="6px" color={TEXT}>
    {children}{required && <Text as="span" color="#EF2E14"> *</Text>}
  </Text>
);

const TransactionCreate = ({ mode = "booking" }) => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id") || "";
  const [editData, setEditData] = useState(null);
  const apartmentId = params.get("apartmentId") || editData?.apartment?._id || "";
  const code = params.get("code") || editData?.apartment?.code || "";
  const projectId = params.get("projectId") || editData?.project?._id || "";
  const eventId = params.get("eventId") || "";

  const isLocking = mode === "locking";
  const backList = isLocking ? `${EMP_BASE_PATH}/tai-chinh/locking` : `${EMP_BASE_PATH}/tai-chinh/booking`;

  const [projectName, setProjectName] = useState("");
  const [note, setNote] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [price, setPrice] = useState("");
  const [bookingTime, setBookingTime] = useState(moment().format("YYYY-MM-DD"));
  const [customer, setCustomer] = useState(null);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Customer picker
  const sheet = useDisclosure();
  const [customers, setCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState("");
  const custTimer = useRef(null);

  // Dự án / Sản phẩm có thể chọn khi không truyền sẵn (booking không chọn căn)
  const projectEditable = !projectId;
  const apartmentEditable = !apartmentId;
  const [pickedProject, setPickedProject] = useState(null);
  const [pickedApartment, setPickedApartment] = useState(null);
  const projSheet = useDisclosure();
  const aptSheet = useDisclosure();
  const [projOpts, setProjOpts] = useState([]);
  const [aptOpts, setAptOpts] = useState([]);
  const [projSearch, setProjSearch] = useState("");
  const [aptSearch, setAptSearch] = useState("");
  const projTimer = useRef(null);
  const aptTimer = useRef(null);

  const effProjectId = projectId || pickedProject?._id || "";
  const effProjectName = projectId ? projectName : pickedProject?.name || "";
  const effApartmentId = apartmentId || pickedApartment?._id || "";
  const effCode = apartmentId ? code : pickedApartment?.code || "";

  useEffect(() => {
    if (projectId) getProjectByIdApi(projectId).then((d) => setProjectName(d?.name || "")).catch(() => {});
  }, [projectId]);

  // Chế độ sửa: nạp dữ liệu booking
  useEffect(() => {
    if (!editId) return;
    getBookingByIdApi(editId).then((d) => {
      setEditData(d);
      setNote(d?.note || "");
      setPrice(d?.price != null ? String(d.price) : "");
      if (d?.bookingTime) setBookingTime(moment(d.bookingTime).format("YYYY-MM-DD"));
      if (d?.customer) setCustomer(d.customer);
      setImages(Array.isArray(d?.transactionImage) ? d.transactionImage : []);
      if (d?.project?.name) setProjectName(d.project.name);
    }).catch(() => {});
  }, [editId]);

  const loadProjects = (kw) => getProjectListApi({ page: 1, limit: 30, search: kw || undefined }).then((d) => setProjOpts(d?.items || d || [])).catch(() => setProjOpts([]));
  const loadApts = (kw) => {
    if (!effProjectId) { setAptOpts([]); return; }
    getProjectApartmentsApi({ projectId: effProjectId, page: 1, limit: 100, search: kw || undefined })
      .then((d) => setAptOpts((d?.items || d || []).map((x) => x.apartment || x)))
      .catch(() => setAptOpts([]));
  };
  const openProj = () => { setProjSearch(""); loadProjects(""); projSheet.onOpen(); };
  const openApt = () => { if (!effProjectId) { toast.error("Vui lòng chọn dự án trước"); return; } setAptSearch(""); loadApts(""); aptSheet.onOpen(); };
  const onProjSearch = (e) => { const v = e.target.value; setProjSearch(v); if (projTimer.current) clearTimeout(projTimer.current); projTimer.current = setTimeout(() => loadProjects(v.trim()), 300); };
  const onAptSearch = (e) => { const v = e.target.value; setAptSearch(v); if (aptTimer.current) clearTimeout(aptTimer.current); aptTimer.current = setTimeout(() => loadApts(v.trim()), 300); };

  const loadCustomers = (kw) => {
    getCustomerListApi({ page: 1, limit: 30, search: kw || undefined })
      .then((d) => setCustomers(d?.items || d || []))
      .catch(() => setCustomers([]));
  };
  const openCustomer = () => { setCustSearch(""); loadCustomers(""); sheet.onOpen(); };
  const onCustSearch = (e) => {
    const v = e.target.value;
    setCustSearch(v);
    if (custTimer.current) clearTimeout(custTimer.current);
    custTimer.current = setTimeout(() => loadCustomers(v.trim()), 300);
  };

  const submit = async () => {
    const e = {};
    if (!note.trim()) e.note = "Vui lòng nhập ghi chú";
    if (projectEditable && !effProjectId) e.project = "Vui lòng chọn dự án";
    if (apartmentEditable && !effApartmentId) e.apartment = "Vui lòng chọn sản phẩm";
    if (!quantity || Number(quantity) <= 0) e.quantity = "Vui lòng nhập số lượng";
    if (!price || Number(price) <= 0) e.price = `Vui lòng nhập số tiền ${isLocking ? "locking" : "booking"}`;
    if (!bookingTime) e.bookingTime = `Vui lòng chọn ngày ${isLocking ? "locking" : "booking"}`;
    if (!customer) e.customer = "Vui lòng chọn khách hàng";
    if (images.length === 0) e.images = "Ảnh giao dịch là trường bắt buộc";
    if (Object.keys(e).length) { setErrors(e); toast.error(Object.values(e)[0]); return; }

    setSubmitting(true);
    try {
      const body = {
        note: note.trim(),
        projectId: effProjectId,
        apartmentId: effApartmentId,
        customerId: customer._id,
        numberBooking: Number(quantity),
        price: Number(price),
        bookingTime,
        transactionImage: images,
        ...(eventId ? { saleOpeningEventId: eventId } : {}),
      };
      if (editId) await updateBookingApi(editId, body);
      else if (isLocking) await createLockingApi(body);
      else await createBookingApi(body);
      toast.success(editId ? "Cập nhật thành công" : isLocking ? "Tạo locking thành công" : "Tạo booking thành công");
      navigate(backList);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Tạo thất bại"));
    } finally {
      setSubmitting(false);
    }
  };

  const clearErr = (k) => setErrors((s) => (s[k] ? { ...s, [k]: undefined } : s));

  const pageBody = (
    <>

      <Box px="16px" pt="16px" flex="1" overflowY="auto" pb="16px">
        <Box mb="16px">
          <Label required>Ghi chú</Label>
          <Text fontSize="12px" color={APP.primary200} mb="6px">( Nhập nhu cầu KH )</Text>
          <Textarea value={note} onChange={(e) => { setNote(e.target.value); clearErr("note"); }} placeholder="Nhập ghi chú" rows={4} fontSize="14px" borderColor={errors.note ? "#EF2E14" : BORDER} borderRadius="8px" />
          {errors.note && <Text fontSize="12px" color="red.500" mt="4px">{errors.note}</Text>}
        </Box>

        <Box mb="16px">
          <Label required>Dự án</Label>
          <Flex as={projectEditable ? "button" : "div"} type={projectEditable ? "button" : undefined} onClick={projectEditable ? openProj : undefined} align="center" justify="space-between" h="46px" px="12px" bg={projectEditable ? "white" : "#F5F7FB"} border="1px solid" borderColor={errors.project ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
            <Text fontSize="14px" color={effProjectName ? TEXT : MUTED} noOfLines={1}>{effProjectName || "Chọn dự án"}</Text>
            <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain", opacity: projectEditable ? 1 : 0.5 }} />
          </Flex>
          {errors.project && <Text fontSize="12px" color="red.500" mt="4px">{errors.project}</Text>}
        </Box>

        <Box mb="16px">
          <Label required>Sản phẩm</Label>
          <Flex as={apartmentEditable ? "button" : "div"} type={apartmentEditable ? "button" : undefined} onClick={apartmentEditable ? openApt : undefined} align="center" justify="space-between" h="46px" px="12px" bg={apartmentEditable ? "white" : "#F5F7FB"} border="1px solid" borderColor={errors.apartment ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
            <Text fontSize="14px" color={effCode ? TEXT : MUTED} noOfLines={1}>{effCode ? `${effCode} (${effCode})` : "Chọn sản phẩm"}</Text>
            <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain", opacity: apartmentEditable ? 1 : 0.5 }} />
          </Flex>
          {errors.apartment && <Text fontSize="12px" color="red.500" mt="4px">{errors.apartment}</Text>}
        </Box>

        <Box mb="16px">
          <Label required>Số lượng</Label>
          <Text fontSize="12px" color={APP.primary200} mb="6px">( Hệ thống sẽ tạo ra số lượng booking với số tiền booking được chia đều theo số tiền booking nhập ở dưới )</Text>
          <Input type="number" value={quantity} onChange={(e) => { setQuantity(e.target.value); clearErr("quantity"); }} placeholder="Nhập số lượng" h="46px" fontSize="14px" borderColor={errors.quantity ? "#EF2E14" : BORDER} borderRadius="8px" />
          {errors.quantity && <Text fontSize="12px" color="red.500" mt="4px">{errors.quantity}</Text>}
        </Box>

        <Box mb="16px">
          <Label required>Số tiền {isLocking ? "locking" : "booking"}</Label>
          <Input type="number" value={price} onChange={(e) => { setPrice(e.target.value); clearErr("price"); }} placeholder={`Nhập số tiền ${isLocking ? "locking" : "booking"}`} h="46px" fontSize="14px" borderColor={errors.price ? "#EF2E14" : BORDER} borderRadius="8px" />
          {errors.price && <Text fontSize="12px" color="red.500" mt="4px">{errors.price}</Text>}
        </Box>

        <Box mb="16px">
          <Label required>Ngày {isLocking ? "locking" : "booking"}</Label>
          <Input type="date" value={bookingTime} onChange={(e) => { setBookingTime(e.target.value); clearErr("bookingTime"); }} h="46px" fontSize="14px" borderColor={errors.bookingTime ? "#EF2E14" : BORDER} borderRadius="8px" />
          {errors.bookingTime && <Text fontSize="12px" color="red.500" mt="4px">{errors.bookingTime}</Text>}
        </Box>

        <Box mb="16px">
          <Label required>Khách hàng</Label>
          <Flex as="button" type="button" onClick={openCustomer} align="center" justify="space-between" h="46px" px="12px" border="1px solid" borderColor={errors.customer ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
            <Text fontSize="14px" color={customer ? TEXT : MUTED} noOfLines={1}>{customer?.name || "Chọn khách hàng"}</Text>
            <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
          </Flex>
          {errors.customer && <Text fontSize="12px" color="red.500" mt="4px">{errors.customer}</Text>}
        </Box>

        <Box mb="20px">
          <Label required>Ảnh giao dịch</Label>
          <ImageUploader value={images} onChange={(v) => { setImages(v); clearErr("images"); }} max={5} />
          {errors.images && <Text fontSize="12px" color="red.500" mt="4px">{errors.images}</Text>}
        </Box>
      </Box>

      <Box borderTop="1px solid #EEF1F6" p="16px">
        <Flex gap="10px">
          <Button flex="1" h="48px" variant="outline" borderColor={APP.primary200} color={APP.primary200} borderRadius="10px" _hover={{ bg: "#EAF2FF" }} onClick={() => navigate(-1)}>
            Hủy
          </Button>
          <Button flex="1" h="48px" bgImage={GRAD} color="white" borderRadius="10px" fontWeight={700} _hover={{ bgImage: GRAD, opacity: 0.92 }} isLoading={submitting} onClick={submit}>
            Thêm
          </Button>
        </Flex>
      </Box>

      {/* Project picker */}
      <Drawer isOpen={projSheet.isOpen} onClose={projSheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">Dự án</Text>
            <Flex h="40px" align="center" px="10px" bg="#F7F9FC" borderRadius="8px" mb="8px">
              <AppIcon src={appImages.icon_search} size={18} color={MUTED} />
              <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Tìm dự án..." value={projSearch} onChange={onProjSearch} />
            </Flex>
            <Box maxH="50vh" overflowY="auto">
              {projOpts.map((p, i) => (
                <Flex key={p._id} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={i === projOpts.length - 1 ? "none" : "1px solid #EEF1F6"} onClick={() => { setPickedProject(p); setPickedApartment(null); setErrors((s) => ({ ...s, project: undefined })); projSheet.onClose(); }}>
                  <Text fontSize="15px" color={pickedProject?._id === p._id ? APP.primary200 : TEXT} textAlign="left">{p.name}</Text>
                  {pickedProject?._id === p._id && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                </Flex>
              ))}
              {projOpts.length === 0 && <Text fontSize="13px" color={MUTED} textAlign="center" py="16px">Không có dự án</Text>}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Apartment picker */}
      <Drawer isOpen={aptSheet.isOpen} onClose={aptSheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">Sản phẩm</Text>
            <Flex h="40px" align="center" px="10px" bg="#F7F9FC" borderRadius="8px" mb="8px">
              <AppIcon src={appImages.icon_search} size={18} color={MUTED} />
              <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Tìm mã căn..." value={aptSearch} onChange={onAptSearch} />
            </Flex>
            <Box maxH="50vh" overflowY="auto">
              {aptOpts.map((a, i) => {
                const disabled = !!a.booking || !!a.locking;
                return (
                  <Flex key={a._id} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={i === aptOpts.length - 1 ? "none" : "1px solid #EEF1F6"} opacity={disabled ? 0.5 : 1} onClick={() => { if (disabled) return; setPickedApartment(a); setErrors((s) => ({ ...s, apartment: undefined })); aptSheet.onClose(); }}>
                    <Text fontSize="15px" color={pickedApartment?._id === a._id ? APP.primary200 : TEXT} textAlign="left">{a.code}{disabled ? (a.booking ? " (Booking)" : " (Locking)") : ""}</Text>
                    {pickedApartment?._id === a._id && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
              {aptOpts.length === 0 && <Text fontSize="13px" color={MUTED} textAlign="center" py="16px">Không có căn hộ</Text>}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Customer picker */}
      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">Khách hàng</Text>
            <Flex h="40px" align="center" px="10px" bg="#F7F9FC" borderRadius="8px" mb="8px">
              <AppIcon src={appImages.icon_search} size={18} color={MUTED} />
              <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Tìm khách hàng..." value={custSearch} onChange={onCustSearch} />
            </Flex>
            <Box maxH="50vh" overflowY="auto">
              {customers.map((c, i) => {
                const active = customer?._id === c._id;
                const last = i === customers.length - 1;
                return (
                  <Flex key={c._id} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={last ? "none" : "1px solid #EEF1F6"} onClick={() => { setCustomer(c); clearErr("customer"); sheet.onClose(); }}>
                    <Box textAlign="left">
                      <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400}>{c.name}</Text>
                      <Text fontSize="12px" color={MUTED}>{c.phone || ""}</Text>
                    </Box>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
              {customers.length === 0 && <Text fontSize="13px" color={MUTED} textAlign="center" py="16px">Không có khách hàng</Text>}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={editId ? "Cập nhật booking" : isLocking ? "Phiếu locking mới" : "Phiếu booking mới"} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title={editId ? "Cập nhật booking" : isLocking ? "Phiếu locking mới" : "Phiếu booking mới"} onBack={() => navigate(-1)} />
      {pageBody}
    </Box>
  );
};

export default TransactionCreate;
